/**
 * How a Markdown file of a project points at other things, as GitHub reads it: a relative link or image is a file of
 * the same project (resolved from the folder of the file that has it; `/x` from the project root), a link with a scheme
 * (`https:`, `mailto:`) leaves Flow, `#section` is a heading of the same file (GitHub's heading slugs). Plus which file
 * is the project's README, shown when nothing else was picked.
 */

/** True for a link that leaves the project: a scheme (`https:`, `mailto:`) or protocol-relative `//host`. */
export const isExternalUrl = (href) => /^[a-z][a-z0-9+.-]*:/i.test(String(href || '')) || String(href || '').startsWith('//');

/**
 * The project file `href` points at, from the Markdown file `fromFile` (project-root-relative, e.g.
 * `services/app/README.md`): `{ path, hash }`, `path` project-root-relative ('' for a link to the root itself, a
 * trailing `/` kept for a folder), `hash` without '#'. Null for an external link, a link to a heading of the same file
 * (`#x`) and one that climbs above the project root.
 */
export function resolveProjectPath(fromFile, href) {
  const raw = String(href || '').trim();
  if (!raw || isExternalUrl(raw) || raw.startsWith('#')) { return null; }
  const hashAt = raw.indexOf('#');
  const hash = hashAt >= 0 ? raw.slice(hashAt + 1) : '';
  let target = hashAt >= 0 ? raw.slice(0, hashAt) : raw;
  const queryAt = target.indexOf('?');
  if (queryAt >= 0) { target = target.slice(0, queryAt); }
  try { target = decodeURIComponent(target); } catch { /* keep it as written */ }
  const folder = String(fromFile || '').replace(/^\.?\/+/, '').split('/').slice(0, -1);
  const parts = target.startsWith('/') ? [] : [...folder];
  for (const segment of target.split('/')) {
    if (segment === '' || segment === '.') { continue; }
    if (segment === '..') {
      if (parts.length === 0) { return null; }
      parts.pop();
    } else {
      parts.push(segment);
    }
  }
  const isFolder = target.endsWith('/') && parts.length > 0;
  return { path: parts.join('/') + (isFolder ? '/' : ''), hash };
}

/** GitHub's slug of a heading's text: lower case, punctuation dropped, spaces as '-' ("Getting started!" → getting-started). */
export const githubSlug = (text) => String(text || '')
  .trim()
  .toLowerCase()
  .replace(/[^\p{L}\p{M}\p{N}\p{Pc} -]/gu, '')
  .replace(/ /g, '-');

/** A slugger for one document: the same heading text twice gets `-1`, `-2`… as on GitHub. */
export function createSlugger() {
  const seen = new Map();
  return (text) => {
    const base = githubSlug(text);
    let slug = base;
    let count = seen.get(base) || 0;
    while (seen.has(slug)) { count += 1; slug = `${base}-${count}`; }
    seen.set(base, count);
    seen.set(slug, 0);
    return slug;
  };
}

const README = /^readme(\.(md|markdown|txt))?$/i;
// Among the READMEs of one folder: Markdown first, then a plain README, then .txt; README.md before readme.md.
const readmeRank = (name) => {
  const ext = (name.match(/\.([^.]+)$/) || [])[1]?.toLowerCase();
  const kind = ext === 'md' ? 0 : ext === 'markdown' ? 1 : !ext ? 2 : 3;
  return kind * 2 + (name === name.toUpperCase().replace(/\.[^.]+$/, (e) => e.toLowerCase()) ? 0 : 1);
};

/**
 * The project's README among `paths` (project-root-relative), as GitHub picks it: the one in the root first, else the
 * least deep one (a service's own, `services/app/README.md`); null when there is none.
 */
export function findReadme(paths) {
  const readmes = (paths || []).filter((path) => README.test(String(path).split('/').pop()));
  if (readmes.length === 0) { return null; }
  const depth = (path) => path.split('/').length;
  return [...readmes].sort((a, b) => depth(a) - depth(b)
    || readmeRank(a.split('/').pop()) - readmeRank(b.split('/').pop())
    || a.localeCompare(b))[0];
}
